import { Injectable, signal } from '@angular/core';

export interface ConfirmationDialogConfig {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class ConfirmationDialogService {
  private dialogConfig = signal<ConfirmationDialogConfig | null>(null);
  private isVisible = signal<boolean>(false);

  readonly config = this.dialogConfig.asReadonly();
  readonly visible = this.isVisible.asReadonly();

  /**
   * Show the confirmation dialog with the provided configuration
   */
  show(config: ConfirmationDialogConfig): void {
    this.dialogConfig.set({
      title: config.title ?? 'Confirmation',
      message: config.message,
      confirmLabel: config.confirmLabel ?? 'Yes',
      cancelLabel: config.cancelLabel ?? 'No',
      onConfirm: config.onConfirm,
      onCancel: config.onCancel
    });
    this.isVisible.set(true);
  }

  /**
   * Hide the confirmation dialog
   */
  hide(): void {
    this.isVisible.set(false);
    // Clear config after a short delay to allow animations to complete
    setTimeout(() => {
      this.dialogConfig.set(null);
    }, 300);
  }

  /**
   * Confirm the action and hide the dialog
   */
  confirm(): void {
    const config = this.dialogConfig();
    if (config) {
      config.onConfirm();
      this.hide();
    }
  }

  /**
   * Cancel the action and hide the dialog
   */
  cancel(): void {
    const config = this.dialogConfig();
    if (config) {
      if (config.onCancel) {
        config.onCancel();
      }
      this.hide();
    }
  }
}

